import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Plus, Trash2, ChevronLeft, Save, ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { ImageUpload } from "@/components/ImageUpload";
import { MarkdownEditor } from "@/components/MarkdownEditor";
import { CategorySelect } from "@/components/CategorySelect";
import { toast } from "sonner";

const input = "w-full bg-transparent border border-border rounded-md px-3 py-2.5 text-sm outline-none focus:border-cobalt transition-colors";

export default function CourseUpload() {
  const { courseId } = useParams();
  const editing = !!courseId;
  const navigate = useNavigate();
  const [options, setOptions] = useState([]);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [cover, setCover] = useState("");
  const [categories, setCategories] = useState([]);
  const [isPaid, setIsPaid] = useState(false);
  const [lessons, setLessons] = useState([{ title: "", content: "", video_url: "" }]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/categories").then((r) => setOptions(r.data)).catch(() => {});
    if (editing) {
      api.get(`/courses/${courseId}`).then((r) => {
        const c = r.data;
        setTitle(c.title); setDescription(c.description || ""); setCover(c.cover_image || "");
        setCategories(c.categories || []); setIsPaid(c.is_paid);
        setLessons(c.lessons.map((l) => ({ title: l.title, content: l.content || "", video_url: l.video_url || "" })));
      });
    }
  }, [courseId, editing]);

  const setLesson = (i, k, v) => setLessons((ls) => ls.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)));
  const addLesson = () => setLessons((ls) => [...ls, { title: "", content: "", video_url: "" }]);
  const removeLesson = (i) => setLessons((ls) => ls.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (title.trim().length < 5) return toast.error("Title must be at least 5 characters");
    if (!lessons.some((l) => l.title.trim())) return toast.error("Add at least one lesson with a title");
    setBusy(true);
    const payload = { title, description, cover_image: cover || null, categories, is_paid: isPaid,
      lessons: lessons.filter((l) => l.title.trim()) };
    try {
      const res = editing
        ? await api.put(`/courses/${courseId}`, payload)
        : await api.post("/courses", payload);
      const { status, reason } = res.data;
      if (status === "published") toast.success("Course published! +40 pts");
      else if (status === "pending") toast(`Sent for review: ${reason}`, { icon: "🕓" });
      else toast.error(`Declined: ${reason}`);
      navigate("/courses");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save course");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <button onClick={() => navigate("/courses")} data-testid="upload-back-button"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
        <ChevronLeft size={16} /> Back to courses
      </button>
      <PageHeader eyebrow="Share your knowledge" title={editing ? "Edit course" : "Upload a course"}
        subtitle="Publish a course for the community. Content is auto-checked for quality and safety before going live." />

      <div className="border border-border bg-card rounded-xl p-6 space-y-5">
        <div>
          <label className="eyebrow text-muted-foreground">Title</label>
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Practical Prompt Engineering for Teams"
            className={`${input} mt-2`} data-testid="course-title-input" />
        </div>
        <div>
          <label className="eyebrow text-muted-foreground">Description</label>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3}
            placeholder="What will members learn? (min 30 characters to auto-publish)"
            className={`${input} mt-2 resize-none`} data-testid="course-description-input" />
        </div>
        <div>
          <label className="eyebrow text-muted-foreground">Cover image</label>
          <div className="mt-2"><ImageUpload value={cover} onChange={setCover} aspect="aspect-[16/9]" label="Upload cover" testid="course-cover" /></div>
        </div>
        <div>
          <label className="eyebrow text-muted-foreground">Categories</label>
          <div className="mt-2"><CategorySelect options={options} value={categories} onChange={setCategories} /></div>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={isPaid} onChange={(e) => setIsPaid(e.target.checked)} data-testid="course-paid-checkbox" />
          Make this a paid course ($49, one-time)
        </label>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-serif text-2xl font-bold">Lessons</h2>
          <button onClick={addLesson} data-testid="add-lesson-button"
            className="inline-flex items-center gap-1.5 border border-border rounded-md px-3 py-2 text-sm hover:bg-secondary/60 transition-colors">
            <Plus size={14} /> Add lesson
          </button>
        </div>
        {lessons.map((l, i) => (
          <div key={i} className="border border-border bg-card rounded-xl p-5 space-y-3" data-testid={`lesson-editor-${i}`}>
            <div className="flex items-center gap-2">
              <span className="font-mono text-xs text-muted-foreground">{i + 1}</span>
              <input value={l.title} onChange={(e) => setLesson(i, "title", e.target.value)} placeholder="Lesson title"
                className={input} data-testid={`lesson-title-${i}`} />
              {lessons.length > 1 && (
                <button onClick={() => removeLesson(i)} className="p-2 text-muted-foreground hover:text-destructive" data-testid={`lesson-remove-${i}`}>
                  <Trash2 size={15} />
                </button>
              )}
            </div>
            <div className="border border-border rounded-md p-3">
              <MarkdownEditor value={l.content} onChange={(v) => setLesson(i, "content", v)}
                placeholder="Lesson content — Markdown supported (min 50 chars in one lesson to auto-publish)" rows={4}
                testid={`lesson-content-${i}`} />
            </div>
            <input value={l.video_url} onChange={(e) => setLesson(i, "video_url", e.target.value)} placeholder="Video URL (optional)"
              className={input} data-testid={`lesson-video-${i}`} />
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3 pt-2">
        <button onClick={submit} disabled={busy} data-testid="submit-course-button"
          className="inline-flex items-center gap-2 bg-cobalt text-white rounded-md px-6 py-3 text-sm font-semibold hover:bg-gold-hover transition-colors disabled:opacity-50">
          <Save size={15} /> {busy ? "Submitting…" : editing ? "Save changes" : "Publish course"}
        </button>
        <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
          <ShieldCheck size={14} className="text-emerald" /> Auto-moderated for quality & safety
        </span>
      </div>
    </div>
  );
}
